import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Box, Button, Card, CardContent, CardHeader, Container, FormControl, FormHelperText, Grid, OutlinedInput, Stack, Typography } from "@mui/material";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import DownloadButton from "../../../Common/Button/DownloadButton";
import MSFAvatar from "../MSFAvatar";
import NewRecipientDialog from "./NewRecipientDialog";
import ProgressBar from "../../../Common/ProgressBar/ProgressBar";
import RecipientsDialog from "./RecipientsDialog";
import RecipientsTable from "./RecipientsTable";
import SurveyFormDialog from "./SurveyFormDialog";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DoneIcon from "@mui/icons-material/Done";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import SendIcon from "@mui/icons-material/Send";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";

import msfActions from "../../../../actions/msfActions";
import { getSurvey, getSurveyReport, getUser } from "../../../../reducers";

import { getSurveyAvatarBadge, surveyStatusMessage } from "./SurveyFunctions";
import { displayFullName } from "../../../../utilities/formatters";
import SurveyDetails from "./SurveyDetails";
import ReportSummary from "../Report/ReportSummary";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);


function Survey({ match }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const appCode = "P-MSF";

    useEffect(() => {
        dispatch(msfActions.getSurveyDetails(appCode, match.params.uuid));
    }, [dispatch, match]);

    const survey = useSelector((state) => getSurvey(state, match.params.uuid));
    const user = useSelector((state) => getUser(state));
    const { url } = useSelector((state) => getSurveyReport(state, match.params.uuid)) || {};

    const title = survey?.title || "360 feedback survey";
    const subTitle = survey?.type?.name || survey.resourceType;

    const isFacilitator = survey?.facilitator?.uuid === user?.uuid;
    const alert = surveyStatusMessage(survey?.status, isFacilitator);
    const isActive = survey?.status !== "completed";

    const maximum = survey?.result?.maximum || null;
    const minimum = survey?.result?.minimum || 0;
    const returned = survey?.result?.returned || 0;

    const assessorsSubTitle =
        maximum > 0
            ? `A maximum of ${maximum} assessors can be included`
            : "No maximum number or assessors";

    const progressData = {
        title: "Responses",
        label: `/ ${minimum}`,
        total: returned,
        percentage: returned > 0 ? Math.min((returned / minimum) * 100, 100) : 0
    };

    const [openDialog, setOpenDialog] = useState(null);
    const [approvedRecipients, setApprovedRecipients] = useState(survey?.recipients?.map(({uuid}) => uuid) || []);
    const [removedRecipient, setRemovedRecipient] = useState(null);
    const [rejectionNotes, setRejectionNotes] = useState(null);

    const removeRecipient = (uuid) => {
        dispatch(
            msfActions.updateSurvey(appCode, survey.uuid, {
                recipients: survey.recipients.filter((recipient) => recipient.uuid !== uuid)
            })
        );
    };

    const submitRecipients = () => {
        dispatch(
            msfActions.updateSurvey(appCode, survey.uuid, {
                recipients: survey.recipients.map((rec) => ({
                    ...rec,
                    nomination: { status: approvedRecipients.includes(rec.uuid) ? "approved" : "rejected" }
                }))
            })
        );
    };

    const rejectSurvey = () => {
        dispatch(
            msfActions.applySurveyAction(appCode, survey.uuid, {
                object: "survey",
                action: "reject",
                data: { reason: rejectionNotes },
            })
        );
    };

    const deleteSurvey = () => {
        dispatch(msfActions.deleteSurvey(appCode, survey.uuid));
        history.replace("/msf/surveys");
        setOpenDialog(null);
    };

    const downloadSurveyReport = (onSuccess, url) => dispatch(msfActions.downloadSurveyReport(appCode, url, onSuccess));


    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            avatar={
                                <MSFAvatar
                                    type="survey"
                                    badge={getSurveyAvatarBadge(survey.status, isFacilitator)}
                                />
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    {!!survey?.canUpdate && (
                                        <Button
                                            variant="contained"
                                            color="neutral"
                                            disableElevation
                                            aria-label="edit survey"
                                            onClick={() => setOpenDialog("edit")}
                                            startIcon={<EditOutlinedIcon />}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {!!survey?.canDelete && (
                                        <Button
                                            variant="contained"
                                            color="neutral"
                                            disableElevation
                                            aria-label="delete survey"
                                            onClick={() => setOpenDialog("delete")}
                                            startIcon={<DeleteOutlinedIcon />}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    {!!isFacilitator && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            aria-label="reject survey"
                                            onClick={() => setOpenDialog("reject")}
                                            startIcon={<CancelOutlinedIcon />}
                                        >
                                            Reject
                                        </Button>
                                    )}
                                </Stack>
                            }
                            title={
                                <Typography component="h3" className="card-header-title" >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    {subTitle}
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <SurveyDetails
                                    requester={survey?.requester}
                                    areaOfWork={survey?.areaOfWork}
                                    organisation={survey?.organisation}
                                    facilitator={survey?.facilitator}
                                    startDate={survey?.startDate}
                                    endDate={survey?.endDate}
                                    selfAssessment={survey?.selfAssessment}
                                    report={survey?.report}
                                    isFacilitator={isFacilitator}
                                    isActive={isActive}
                                />
                            </Box>
                        </CardContent>

                        {alert?.message && (
                            <CardContent>
                                <Box className="card-content">
                                    <AlertMessage
                                        severity={alert.severity}
                                        message={alert.message}
                                    />
                                </Box>
                            </CardContent>
                        )}

                        <CardContent>
                            <Box className="card-content">
                                <ProgressBar data={progressData} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    {!!survey?.canUpdateRecipients && (
                                        <Button
                                            variant="contained"
                                            color="neutral"
                                            disableElevation
                                            aria-label="edit assessors"
                                            onClick={() => setOpenDialog("addRecipients")}
                                            startIcon={<PersonAddOutlinedIcon />}
                                        >
                                            Add
                                        </Button>
                                    )}

                                    {!isFacilitator
                                        && ["draft", "open"].includes(survey?.status)
                                        && survey?.recipients?.findIndex(({nomination}) => nomination.status === "proposed") >= 0 && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disableElevation
                                                aria-label="submit assessors"
                                                endIcon={<SendIcon />}
                                                onClick={() => setOpenDialog("submitRecipients")}
                                            >
                                                Submit
                                            </Button>
                                        )}

                                    {isFacilitator
                                        && survey?.status === "awaiting_approval" && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disableElevation={true}
                                                aria-label="review assessors"
                                                onClick={() => setOpenDialog("reviewAssessors")}
                                                endIcon={<FileDownloadDoneIcon />}
                                            >
                                                Review
                                            </Button>
                                        )}
                                </Stack>
                            }
                            title={
                                <Typography component="h3" className="card-header-title" >
                                    Assessors
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    {assessorsSubTitle}
                                </Typography>
                            }
                        />

                        <CardContent>
                            <Box className="card-content">
                                {!!survey?.recipients && (
                                    <RecipientsTable
                                        isFacilitator={isFacilitator}
                                        recipients={survey.recipients}
                                        userId={survey.requester?.uuid}
                                        onDelete={
                                            !survey.canUpdateRecipients
                                                ? null
                                                : (uuid) => {
                                                      setRemovedRecipient(uuid);
                                                      setOpenDialog("removeRecipient");
                                                  }
                                        }
                                        onApprove={(recipient, approved) => {
                                            if (!approved) {
                                                setApprovedRecipients(approvedRecipients.filter((uuid) => uuid !== recipient));
                                            } else if (!approvedRecipients.includes(recipient)) {
                                                setApprovedRecipients([...approvedRecipients, recipient]);
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {["review", "completed"].includes(survey?.report?.status) && (
                    <Grid item xs={12}>
                        <Card className="card">
                            <CardHeader
                                disableTypography
                                title={
                                    <Typography component="h3" className="card-header-title" >
                                        Report
                                    </Typography>
                                }
                                subheader={
                                    <Typography component="span" className="card-header-subtitle" >
                                        Summary
                                    </Typography>
                                }
                                action={
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        spacing={2}
                                        sx={{ padding: "8px" }}
                                    >
                                        {!isFacilitator ? null : (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disableElevation
                                                aria-label="release report"
                                                onClick={() => setOpenDialog("reviewReport")}
                                                endIcon={<DoneIcon/>}
                                            >
                                                Release
                                            </Button>
                                        )}
                                        {!!url && (
                                            <DownloadButton
                                                url={url}
                                                filename={`${title}-Report`}
                                                download={downloadSurveyReport}
                                            />
                                        )}
                                    </Stack>
                                }
                            />
                            <CardContent>
                                <ReportSummary
                                    surveyId={survey.uuid}
                                    canFetch={isFacilitator || survey.report.status === 'completed'}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
            {openDialog === "edit" && (
                <SurveyFormDialog
                    open
                    onClose={() => setOpenDialog(null)}
                    data={survey}
                />
            )}
            {openDialog === "removeRecipient" && !!removedRecipient && (
                <ConfirmDialog
                    open
                    title="Remove assessor"
                    content={`Remove ${displayFullName(survey.recipients.find(({uuid}) => removedRecipient === uuid))}?`}
                    confirmButtonLabel="Remove"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={() => {
                        removeRecipient(removedRecipient);
                        setRemovedRecipient(null);
                        setOpenDialog(null);
                    }}
                />
            )}
            {openDialog === "delete" && (
                <ConfirmDialog
                    open
                    title="Delete survey"
                    content={`Delete ${title}?`}
                    confirmButtonLabel="Delete"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={deleteSurvey}
                />
            )}
            {openDialog === "reject" && (
                <ConfirmDialog
                    open
                    title="Reject survey"
                    content={`Reject ${title}?`}
                    confirmButtonLabel="Reject"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={!rejectionNotes ? null : rejectSurvey}
                >
                    <FormControl component="div" fullWidth margin="normal">
                        <OutlinedInput
                            value={rejectionNotes}
                            onChange={({target}) => setRejectionNotes(target.value)}
                            placeholder="Comments *"
                            multiline
                            rows={2}
                        />
                        <FormHelperText>
                            Please specify why this survey is being rejected
                        </FormHelperText>
                    </FormControl>
                </ConfirmDialog>
            )}
            {openDialog === "addRecipients" && (
                <RecipientsDialog
                    open
                    currentRecipients={survey.recipients}
                    onCancel={() => setOpenDialog(null)}
                    onClose={() => setOpenDialog(null)}
                    onSave={(recipients) => {
                        dispatch(
                            msfActions.updateSurvey(appCode, survey.uuid, {
                                recipients: [
                                    ...survey.recipients,
                                    ...recipients,
                                ],
                            })
                        );
                    }}
                    onAddCustom={() => setOpenDialog("newRecipient")}
                    max={maximum}
                />
            )}
            {openDialog === "newRecipient" && (
                <NewRecipientDialog
                    open
                    onClose={() => setOpenDialog(null)}
                    onSave={(recipient) => {
                        dispatch(
                            msfActions.updateSurvey(appCode, survey.uuid, {
                                recipients: [...survey.recipients, recipient]
                            })
                        );
                    }}
                />
            )}
            {openDialog === "submitRecipients" && (
                <ConfirmDialog
                    open
                    title="Submit assessors"
                    content="Submit assessors for approval?"
                    confirmButtonLabel="Submit"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={() => {
                        dispatch(
                            msfActions.applySurveyAction(appCode, survey.uuid, {
                                object: "recipients",
                                action: "request_approval"
                            })
                        );
                    }}
                />
            )}
            {openDialog === "reviewAssessors" && (
                <ConfirmDialog
                    open
                    title="Assessors review"
                    content="Review the current list of assessors"
                    confirmButtonLabel="Submit"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={submitRecipients}
                />
            )}
            {openDialog === "reviewReport" && (
                <ConfirmDialog
                    open
                    title="Release report"
                    content="Please review and release this report."
                    confirmButtonLabel="Release"
                    onClose={() => setOpenDialog(null)}
                    onCancel={() => setOpenDialog(null)}
                    onConfirm={() => {
                        dispatch(
                            msfActions.applySurveyAction(appCode, survey.uuid, {
                                object: "report",
                                action: "release"
                            })
                        );
                    }}
                />
            )}
        </Container>
    );
}

export default Survey;
